import {
  TypeImplementation,
  withSystem,
  Plugins,
  EnvTypes,
} from '@packages/systems/wfdl';
import {
  functionOf,
  TTypeConstructor,
  TList,
  TText,
  TListOfElement,
} from '@packages/systems/wfdl-ast';
import {iconName, qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {TOption} from '@packages/systems/plugins/Option';
import {type DynamoEnvValueType} from '../types';
import {TPredicate, FALSE, TRUE} from '@packages/systems/plugins-utils/Boolean';
import {operand} from '@packages/systems/plugins/ConditionsOperand';
import {
  TDynamoImage,
  TDynamoMultiImage,
} from '@packages/systems/plugins-utils/DynamoGateway';

export const ImageType = TypeImplementation.Custom({
  render: () => null,
  icon: TypeImplementation.IconByName(
    qualify(Plugins.name('Basic'), iconName('Image'))
  ),
});

export const MultiImageType = TypeImplementation.Alias({
  type: TList(TDynamoImage),
  render: () => null,
  icon: TypeImplementation.IconByName(
    qualify(Plugins.name('DynamoGateway'), iconName('MultiImage'))
  ),
});

const TImage = TTypeConstructor(
  qualify(Plugins.name('Asset'), typeName('Image'))
);

const emptyImageLiteral = Object.freeze({
  id: '',
});

function toLightboxMediaImage(val: unknown) {
  if (val !== null && typeof val === 'object' && 'url' in val) {
    // We only pass image url to preserve previous functionality
    return {
      tag: 'Image',
      val: {
        image: {url: val.url},
      },
    };
  }

  return null;
}

export const dynamoImageToImage: EnvTypes.TypedValue = {
  envType: {
    dataType: functionOf(
      withSystem({isBindable: true}, TOption(TDynamoImage)),
      TImage
    ),
    // TODO (platform): Set as 'hidden' when bindings UI update ships
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value(option: DynamoEnvValueType<unknown>) {
    if (option === null || option.tag === 'None') {
      return emptyImageLiteral;
    }

    const {val} = option;
    if (val === null) {
      return emptyImageLiteral;
    }

    if (typeof val === 'object' && 'url' in val) {
      return val;
    }

    return emptyImageLiteral;
  },
};

const emptyListLiteral = Object.freeze([]);

export const dynamoImageToText: EnvTypes.TypedValue = {
  envType: {
    dataType: functionOf(
      withSystem({isBindable: true}, TOption(TDynamoImage)),
      TText
    ),
    exported: 'hidden',
    implicit: 'prefilledArgs',
  },
  value(option: DynamoEnvValueType<unknown>) {
    if (option === null || option.tag === 'None') return '';
    const {val} = option;
    if (
      val !== null &&
      typeof val === 'object' &&
      'url' in val &&
      typeof val.url === 'string'
    ) {
      return val.url;
    }
    return '';
  },
};

export const dynamoImageToListOfElements: EnvTypes.TypedValue = {
  envType: {
    dataType: functionOf(
      withSystem({isBindable: true}, TOption(TDynamoImage)),
      TListOfElement
    ),
    exported: 'hidden',
    implicit: 'prefilledArgs',
  },
  value(option: DynamoEnvValueType<unknown>) {
    if (option === null || option.tag === 'None') return emptyListLiteral;
    const {val} = option;
    if (
      val !== null &&
      typeof val === 'object' &&
      'url' in val &&
      typeof val.url === 'string'
    ) {
      return [
        {
          text: true,
          type: null,
          id: 'temp',
          children: [],
          data: {value: val.url},
        },
      ];
    }
    return emptyListLiteral;
  },
};

export const dynamoImageToListOfLightboxMedia: EnvTypes.TypedValue = {
  envType: {
    dataType: functionOf(
      withSystem({isBindable: true}, TOption(TDynamoImage)),
      TList(
        TTypeConstructor(
          qualify(Plugins.name('Lightbox'), typeName('LightboxMedia'))
        )
      )
    ),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value(option: DynamoEnvValueType<unknown>) {
    if (option === null || option.tag === 'None') {
      return emptyListLiteral;
    }

    const maybeLightboxMedia = toLightboxMediaImage(option.val);

    if (maybeLightboxMedia) {
      return [maybeLightboxMedia];
    }

    return emptyListLiteral;
  },
};

export const listOfDynamoImageToListOfLightboxMedia: EnvTypes.TypedValue = {
  envType: {
    dataType: functionOf(
      withSystem({isBindable: true}, TOption(TList(TDynamoImage))),
      TList(
        TTypeConstructor(
          qualify(Plugins.name('Lightbox'), typeName('LightboxMedia'))
        )
      )
    ),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value(option: DynamoEnvValueType<unknown>) {
    if (option === null || option.tag === 'None') {
      return emptyListLiteral;
    }

    const {val} = option;
    if (!Array.isArray(val) || val.length === 0) {
      return emptyListLiteral;
    }

    return val.reduce((listOfLightboxMedia, item) => {
      const maybeLightboxMedia = toLightboxMediaImage(item);
      if (maybeLightboxMedia) {
        listOfLightboxMedia.push(maybeLightboxMedia);
      }
      return listOfLightboxMedia;
    }, []);
  },
};

export const dynamoMultiImageIsSet: EnvTypes.TypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'is set'},
      TPredicate(TOption(TDynamoMultiImage))
    ),
    exported: 'visible',
  },
  value: (option: DynamoEnvValueType<string>) => {
    if (
      option === null ||
      option.tag === 'None' ||
      (option.tag === 'Some' && option.val.length === 0)
    ) {
      return FALSE;
    }

    return TRUE;
  },
};

export const dynamoMultiImageIsNotSet: EnvTypes.TypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'is not set'},
      TPredicate(TOption(TDynamoMultiImage))
    ),
    exported: 'visible',
  },
  value: (option: DynamoEnvValueType<string>) => {
    if (
      option === null ||
      option.tag === 'None' ||
      (option.tag === 'Some' && option.val.length === 0)
    ) {
      return TRUE;
    }

    return FALSE;
  },
};

export const dynamoMultiImageOperand = operand(TOption(TDynamoMultiImage));
